import React, { memo, useState } from 'react';
import {
  Box, Table, TableHead, TableRow, TableCell, TableBody,
  Typography, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Grid,
} from '@mui/material';
import StatusChip from '../StatusChip';
import InvButton from '../InvButton';
import { InvTextField, InvSelect } from '../InvField';
import {
  startQuoteEvaluation, evaluateVendorQuote,
} from '../../api/rfq';
import {
  toEvaluateQuoteBody, canAcceptQuote, vendorDisplayLabel,
} from '../../utils/rfqEntity';
import { QUOTE_EVALUATION_STATUSES } from '../../constants/rfqManagement';
import { formatCurrency } from '../../utils';
import { IPD_COLORS, cardSx } from '../../theme';

function EvaluateDialog({ open, quote, onClose, onSaved }) {
  const quoteId = quote?.vendor_quote_id || quote?.id;
  const [technicalScore, setTechnicalScore] = useState('80');
  const [commercialScore, setCommercialScore] = useState('90');
  const [evaluationStatus, setEvaluationStatus] = useState('RECOMMENDED');
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleSave() {
    setLoading(true);
    setError(null);
    try {
      await evaluateVendorQuote(quoteId, toEvaluateQuoteBody({
        technical_score: technicalScore,
        commercial_score: commercialScore,
        evaluation_status: evaluationStatus,
        remarks,
      }));
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e.message || 'Evaluation failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth PaperProps={{ sx: cardSx }}>
      <DialogTitle sx={{ color: IPD_COLORS.textPrimary }}>Evaluate Quote</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mb: 2 }}>
          {quote?.quote_number} — {quote?.vendor_name || quote?.vendor_id}
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <InvTextField label="Technical Score" type="number" inputProps={{ min: 0, max: 100 }}
              value={technicalScore} onChange={(e) => setTechnicalScore(e.target.value)} />
          </Grid>
          <Grid item xs={6}>
            <InvTextField label="Commercial Score" type="number" inputProps={{ min: 0, max: 100 }}
              value={commercialScore} onChange={(e) => setCommercialScore(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <InvSelect
              label="Evaluation Status"
              value={evaluationStatus}
              onChange={(e) => setEvaluationStatus(e.target.value)}
              options={QUOTE_EVALUATION_STATUSES}
            />
          </Grid>
          <Grid item xs={12}>
            <InvTextField label="Remarks" multiline rows={2} value={remarks}
              onChange={(e) => setRemarks(e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSave} disabled={loading} variant="contained"
          sx={{ backgroundColor: IPD_COLORS.primary }}>
          Save Evaluation
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RfqEvaluationPanel({
  evaluation,
  quotes = [],
  vendorDirectory = [],
  loading,
  error,
  onRefresh,
  onAward,
}) {
  const [evalTarget, setEvalTarget] = useState(null);
  const [actioningId, setActioningId] = useState(null);

  const evalRows = evaluation?.quote_evaluations || [];
  const recommendedId = evaluation?.recommended_quote_id;

  function vendorLabel(vendorId) {
    const match = vendorDirectory.find((v) => (v.vendor_id || v.id) === vendorId);
    return match ? vendorDisplayLabel(match) : vendorId;
  }

  async function handleStartEvaluation(quote) {
    const quoteId = quote.vendor_quote_id || quote.id;
    setActioningId(quoteId);
    try {
      await startQuoteEvaluation(quoteId);
      onRefresh?.();
    } finally {
      setActioningId(null);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} sx={{ color: IPD_COLORS.primary }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const displayRows = evalRows.length > 0 ? evalRows : quotes;

  return (
    <Box>
      {recommendedId && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Recommended quote ID: <strong>{recommendedId}</strong>
        </Alert>
      )}

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Vendor</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Quote #</TableCell>
              <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Tech</TableCell>
              <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Commercial</TableCell>
              <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Total</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: 'center', color: IPD_COLORS.textSecondary }}>
                  No quotes to evaluate yet.
                </TableCell>
              </TableRow>
            ) : displayRows.map((row, i) => {
              const quoteId = row.vendor_quote_id || row.id;
              const isRecommended = quoteId === recommendedId
                || row.evaluation_status === 'RECOMMENDED';
              return (
                <TableRow
                  key={quoteId || i}
                  sx={isRecommended ? { backgroundColor: IPD_COLORS.success.bg } : undefined}
                >
                  <TableCell>{row.vendor_name || vendorLabel(row.vendor_id)}</TableCell>
                  <TableCell>{row.quote_number || '—'}</TableCell>
                  <TableCell align="right">{row.technical_score ?? '—'}</TableCell>
                  <TableCell align="right">{row.commercial_score ?? '—'}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(row.total_net_amount)}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={row.evaluation_status || row.status} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {row.status === 'SUBMITTED' && (
                        <InvButton size="small" variant="outlined" tone="teal"
                          loading={actioningId === quoteId}
                          onClick={() => handleStartEvaluation(row)}>
                          Start Eval
                        </InvButton>
                      )}
                      {['IN_EVALUATION', 'SUBMITTED'].includes(row.status) && !row.technical_score && (
                        <InvButton size="small" tone="teal"
                          onClick={() => setEvalTarget(row)}>
                          Score
                        </InvButton>
                      )}
                      {canAcceptQuote(row) && (
                        <InvButton size="small" tone="mint" onClick={() => onAward?.(row)}>
                          Accept
                        </InvButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <EvaluateDialog
        open={Boolean(evalTarget)}
        quote={evalTarget}
        onClose={() => setEvalTarget(null)}
        onSaved={onRefresh}
      />
    </Box>
  );
}

export default memo(RfqEvaluationPanel);
